import { type LinkComponentProps, createLink } from '@tanstack/react-router';

import { Button, type ButtonProps } from 'gooey';

export type ButtonRouterLinkProps = LinkComponentProps<ButtonProps> &
  Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>;

export type ButtonAnchorLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & ButtonProps;

export type ButtonLinkProps<P extends ButtonRouterLinkProps | ButtonAnchorLinkProps> = P & {
  openInNewTab?: boolean;
};

/**
 * Universal button link component that can be used to link to internal or external URLs.
 */
export function ButtonLink<P extends ButtonRouterLinkProps | ButtonAnchorLinkProps>(
  props: ButtonLinkProps<P>
) {
  if (isRouterLink(props)) {
    return (
      <ButtonRouterLink
        preload='intent'
        {...buildOpenInNewTabProps(props)}
        {...props}
        component='a'
      />
    );
  }

  return <Button {...buildOpenInNewTabProps(props)} {...props} component='a' />;
}

/*
|------------------
| Internals
|------------------
*/

const ButtonRouterLink = createLink(Button);

function isRouterLink(
  props: ButtonRouterLinkProps | ButtonAnchorLinkProps
): props is ButtonRouterLinkProps {
  return 'to' in props;
}

function buildOpenInNewTabProps(
  props: ButtonLinkProps<ButtonRouterLinkProps | ButtonAnchorLinkProps>
) {
  const { openInNewTab = false } = props;

  if (openInNewTab) {
    return {
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }
  return null;
}
